/**
 * CompactCardHeader - Compact card content for the Expanding Card
 *
 * Shows:
 * - Tail number (click to copy) + expand/close buttons
 * - Aircraft type (human name)
 * - Owner / Client name
 * - Current location on its own line (area · unit)
 * - Unified status rows (contract, fuel, warnings — replaces old 3-component system)
 * - Context-sensitive quick action buttons (with departure/arrival flows)
 */

import { useState } from 'react'
import { X, Maximize2, Fuel, LogOut, LogIn, FilePlus, MoreHorizontal, Copy, History, Printer, PlaneTakeoff, PlaneLanding, CalendarClock, ArrowLeftRight, ClipboardList, Pencil, Move } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { StatusRowStack } from './StatusRowStack'
import { FlightLines } from './FlightLines'
import { AwayDetailsPreview } from './AwayDetailsPreview'
import { QuickAwayDialog } from './QuickAwayDialog'
import { LeaseDetailPanel, LeaseMutateDrawer } from '@/components/leases'
import { TransientStayDetailPanel, TransientStayMutateDrawer } from '@/components/transient-stays'
import { formatClientName } from '@/lib/utils/format-business-data'
import { useAircraftCardStore } from '@/stores/aircraftCardStore'
import { useAircraftEditorStore } from '@/stores/aircraftEditorStore'
import { useAppContext } from '@/hooks/routing/useAppContext'
import type { AllocationEnrichedData } from '@/types/aircraft-card'
import { DepartureDialog } from '@/components/ramp-wizard/DepartureDialog'
import { ArrivalDialog } from '@/components/ramp-wizard/ArrivalDialog'
import { ScheduleEditDialog } from '@/components/ramp-wizard/ScheduleEditDialog'
import { FuelTransactionDrawer } from '@/components/fuel-transactions'
import type { FuelTransactionPrefill } from '@/components/fuel-transactions'
import { useCheckInEvent, useCheckOutEvent } from '@/hooks/useAllocationEvents'

/** Map lease fuel_price_type → fuel_pricing_type for transaction prefill */
function mapLeasePriceType(
  fuelPriceType: string | null | undefined
): FuelTransactionPrefill['pricingType'] {
  switch (fuelPriceType) {
    case 'fixed':
    case 'opis_plus':
      return 'contract'
    case 'retail_discount':
      return 'retail'
    default:
      return null
  }
}

interface CompactCardHeaderProps {
  data: AllocationEnrichedData
}

/** Derive the relationship type for this aircraft */
function getRelationship(data: AllocationEnrichedData) {
  if (data.leaseContract) return 'based' as const
  if (data.transientStay) return 'transient' as const
  return 'none' as const
}


export function CompactCardHeader({ data }: CompactCardHeaderProps) {
  const closeCard = useAircraftCardStore((s) => s.closeCard)
  const openFullModal = useAircraftCardStore((s) => s.openFullModal)
  const openEditForm = useAircraftCardStore((s) => s.openEditForm)
  const { locationId } = useAppContext()

  // Dialog state
  const [departureOpen, setDepartureOpen] = useState(false)
  const [arrivalOpen, setArrivalOpen] = useState(false)
  const [checkInConfirmOpen, setCheckInConfirmOpen] = useState(false)
  const [checkOutConfirmOpen, setCheckOutConfirmOpen] = useState(false)
  const [scheduleCreateOpen, setScheduleCreateOpen] = useState(false)
  // Details preview dialogs (Card → Details Preview → Edit Form pattern)
  const [leasePreviewOpen, setLeasePreviewOpen] = useState(false)
  const [leaseEditDrawerOpen, setLeaseEditDrawerOpen] = useState(false)
  const [transientPreviewOpen, setTransientPreviewOpen] = useState(false)
  const [transientEditDrawerOpen, setTransientEditDrawerOpen] = useState(false)
  const [createLeaseOpen, setCreateLeaseOpen] = useState(false)
  const [createStayOpen, setCreateStayOpen] = useState(false)
  const [awayPreviewOpen, setAwayPreviewOpen] = useState(false)
  const [quickAwayOpen, setQuickAwayOpen] = useState(false)
  const [fuelDrawerOpen, setFuelDrawerOpen] = useState(false)

  // Check-In/Out mutations
  const checkInMutation = useCheckInEvent()
  const checkOutMutation = useCheckOutEvent()

  const relationship = getRelationship(data)

  const ownerName = data.aircraft.owner
    ? formatClientName(data.aircraft.owner)
    : data.aircraft.ownerName || null

  const typeName = data.aircraft.model || data.aircraft.typeCode

  const locationParts = [data.allocation.areaName, data.allocation.unitCode].filter(Boolean)
  const locationName = locationParts.length > 0 ? locationParts.join(' · ') : 'Unassigned'

  // Presence-based conditions
  const isPresent = data.allocation.presence === 'present'
  const isAway = data.allocation.presence === 'away'
  const isExpected = data.allocation.presence === 'expected'

  const handleCopyTail = () => {
    navigator.clipboard.writeText(data.aircraft.tailNumber)
    toast.success(`Copied ${data.aircraft.tailNumber}`)
  }

  const handleComingSoon = (action: string) => {
    toast.info(`${action} — coming soon`)
  }

  const handleCheckIn = () => {
    if (!locationId) return
    checkInMutation.mutate(
      {
        allocation_id: data.allocationId,
        aircraft_id: data.aircraft.id,
        location_id: locationId,
        transient_stay_id: data.transientStay?.id ?? null,
        notes: 'Checked in via ramp view',
      },
      {
        onSuccess: () => {
          toast.success(`${data.aircraft.tailNumber} checked in`)
          setCheckInConfirmOpen(false)
        },
        onError: (err) => toast.error(`Check-in failed: ${err.message}`),
      }
    )
  }

  const handleCheckOut = () => {
    if (!locationId) return
    checkOutMutation.mutate(
      {
        allocation_id: data.allocationId,
        aircraft_id: data.aircraft.id,
        location_id: locationId,
        transient_stay_id: data.transientStay?.id ?? null,
        notes: 'Checked out via ramp view',
      },
      {
        onSuccess: () => {
          toast.success(`${data.aircraft.tailNumber} checked out`)
          setCheckOutConfirmOpen(false)
          closeCard()
        },
        onError: (err) => toast.error(`Check-out failed: ${err.message}`),
      }
    )
  }

  return (
    <div className="w-[280px] flex flex-col">
      {/* Row 1: Tail number + type + badges + action icons */}
      <div className="flex items-center justify-between px-3 pt-2.5 pb-0.5 gap-1">
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <span
            className="text-base font-bold cursor-pointer hover:text-primary transition-colors shrink-0"
            onClick={handleCopyTail}
            title="Click to copy"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCopyTail() }}
          >
            {data.aircraft.tailNumber}
          </span>
          <span className="text-xs text-muted-foreground truncate">{typeName}</span>
          {relationship === 'based' && (
            <Badge
              variant="outline"
              className="text-[9px] px-1.5 py-0 shrink-0 border-green-300 text-green-700 bg-green-50 dark:border-green-700 dark:text-green-400 dark:bg-green-950 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setLeasePreviewOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setLeasePreviewOpen(true) }}
            >
              Lease
            </Badge>
          )}
          {relationship === 'transient' && (
            <Badge
              variant="outline"
              className="text-[9px] px-1.5 py-0 shrink-0 border-blue-300 text-blue-700 bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:bg-blue-950 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setTransientPreviewOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setTransientPreviewOpen(true) }}
            >
              Transient
            </Badge>
          )}
          {isAway && (
            <Badge
              variant="outline"
              className="text-[9px] px-1.5 py-0 shrink-0 border-indigo-300 text-indigo-700 bg-indigo-50 dark:border-indigo-700 dark:text-indigo-400 dark:bg-indigo-950 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setAwayPreviewOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setAwayPreviewOpen(true) }}
            >
              Away
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => openFullModal()}
            title="Expand to full view"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={closeCard}
            title="Close"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Row 2: Owner · Location */}
      <div className="px-3 pb-1.5">
        <div className="text-xs text-muted-foreground truncate">
          {ownerName ? (
            <><span className="text-foreground font-medium">{ownerName}</span> · {locationName}</>
          ) : (
            locationName
          )}
        </div>
      </div>

      {/* Unified status rows (contract, fuel, warnings) */}
      <div className="pb-2">
        <StatusRowStack data={data} onLeaseDetailsClick={() => setLeasePreviewOpen(true)} onTransientDetailsClick={() => setTransientPreviewOpen(true)} />
      </div>

      {/* Flight lines (next departure/arrival) */}
      <FlightLines data={data} />

      {/* Quick-log toolbar — always visible */}
      <div className="px-3 py-2 border-t bg-muted/10 flex items-center gap-3">
        <span className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider">Log:</span>
        <div className="flex items-center gap-1">
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-blue-600 hover:bg-blue-50 dark:hover:text-blue-400 dark:hover:bg-blue-950"
                  onClick={() => setFuelDrawerOpen(true)}
                >
                  <Fuel className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Add Fuel Record</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50 dark:hover:text-emerald-400 dark:hover:bg-emerald-950"
                  onClick={() => setScheduleCreateOpen(true)}
                >
                  <ArrowLeftRight className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Schedule Departure / Arrival</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-orange-600 hover:bg-orange-50 dark:hover:text-orange-400 dark:hover:bg-orange-950"
                  onClick={() => handleComingSoon('Service Request')}
                >
                  <ClipboardList className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Request Service</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-purple-600 hover:bg-purple-50 dark:hover:text-purple-400 dark:hover:bg-purple-950"
                  onClick={() => openEditForm('aircraft-notes')}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Add Note</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-amber-600 hover:bg-amber-50 dark:hover:text-amber-400 dark:hover:bg-amber-950"
                  onClick={() => useAircraftEditorStore.getState().startCursorFollow(data.allocationId)}
                >
                  <Move className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Move Aircraft</TooltipContent>
            </Tooltip>

            {isPresent && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-indigo-600 hover:bg-indigo-50 dark:hover:text-indigo-400 dark:hover:bg-indigo-950"
                    onClick={() => setQuickAwayOpen(true)}
                  >
                    <PlaneTakeoff className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Mark Away</TooltipContent>
              </Tooltip>
            )}

            {isAway && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-green-600 hover:bg-green-50 dark:hover:text-green-400 dark:hover:bg-green-950"
                    onClick={() => setArrivalOpen(true)}
                  >
                    <PlaneLanding className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Mark Here</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </div>

        {/* Overflow menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto text-muted-foreground">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={handleCopyTail}>
              <Copy className="w-3.5 h-3.5 mr-2" />Copy Tail #
            </DropdownMenuItem>
            {isExpected && (
              <DropdownMenuItem onClick={() => setCheckInConfirmOpen(true)} disabled={checkInMutation.isPending}>
                <LogIn className="w-3.5 h-3.5 mr-2" />Check In
              </DropdownMenuItem>
            )}
            {isPresent && (
              <DropdownMenuItem onClick={() => setCheckOutConfirmOpen(true)} disabled={checkOutMutation.isPending}>
                <LogOut className="w-3.5 h-3.5 mr-2" />Check Out
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setScheduleCreateOpen(true)}>
              <CalendarClock className="w-3.5 h-3.5 mr-2" />Schedule Departure
            </DropdownMenuItem>
            {relationship === 'none' && (
              <>
                <DropdownMenuItem onClick={() => setCreateLeaseOpen(true)}>
                  <FilePlus className="w-3.5 h-3.5 mr-2" />Create Lease
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCreateStayOpen(true)}>
                  <FilePlus className="w-3.5 h-3.5 mr-2" />Create Stay
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuItem onClick={() => handleComingSoon('View History')}>
              <History className="w-3.5 h-3.5 mr-2" />View History
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleComingSoon('Print Ticket')}>
              <Printer className="w-3.5 h-3.5 mr-2" />Print Ticket
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Departure Dialog */}
      {locationId && (
        <DepartureDialog
          open={departureOpen}
          onOpenChange={setDepartureOpen}
          tailNumber={data.aircraft.tailNumber}
          allocationId={data.allocationId}
          aircraftId={data.aircraft.id}
          locationId={locationId}
        />
      )}

      {/* Arrival Dialog */}
      {locationId && (
        <ArrivalDialog
          open={arrivalOpen}
          onOpenChange={setArrivalOpen}
          tailNumber={data.aircraft.tailNumber}
          allocationId={data.allocationId}
          aircraftId={data.aircraft.id}
          locationId={locationId}
        />
      )}

      {/* Quick Away Dialog */}
      {locationId && (
        <QuickAwayDialog
          open={quickAwayOpen}
          onOpenChange={setQuickAwayOpen}
          tailNumber={data.aircraft.tailNumber}
          allocationId={data.allocationId}
          aircraftId={data.aircraft.id}
          locationId={locationId}
        />
      )}

      {/* Check-In Confirmation */}
      <AlertDialog open={checkInConfirmOpen} onOpenChange={setCheckInConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Check In {data.aircraft.tailNumber}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark {data.aircraft.tailNumber} as present on the map and activate its allocation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCheckIn} disabled={checkInMutation.isPending}>
              {checkInMutation.isPending ? 'Checking in...' : 'Check In'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Check-Out Confirmation */}
      <AlertDialog open={checkOutConfirmOpen} onOpenChange={setCheckOutConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Check Out {data.aircraft.tailNumber}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark {data.aircraft.tailNumber} as departed and remove it from the map. This action completes the allocation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCheckOut} disabled={checkOutMutation.isPending}>
              {checkOutMutation.isPending ? 'Checking out...' : 'Check Out'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Lease Detail Panel (Card → Preview → Edit pattern) */}
      <LeaseDetailPanel
        leaseId={data.leaseContract?.id}
        open={leasePreviewOpen}
        onOpenChange={setLeasePreviewOpen}
        onEdit={() => {
          setLeasePreviewOpen(false)
          setLeaseEditDrawerOpen(true)
        }}
        onCheckOut={data.allocation.status !== 'completed' ? () => {
          setLeasePreviewOpen(false)
          setCheckOutConfirmOpen(true)
        } : undefined}
        isCheckOutLoading={checkOutMutation.isPending}
      />

      {/* Lease Edit Drawer (opened from LeaseDetailPanel's Edit button) */}
      {data.leaseContract && (
        <LeaseMutateDrawer
          open={leaseEditDrawerOpen}
          onOpenChange={setLeaseEditDrawerOpen}
          leaseId={data.leaseContract.id}
          lockedLocationId={locationId ?? undefined}
        />
      )}

      {/* Transient Stay Detail Panel (Card → Preview → Edit pattern) */}
      <TransientStayDetailPanel
        stayId={data.transientStay?.id}
        open={transientPreviewOpen}
        onOpenChange={setTransientPreviewOpen}
        onEdit={() => {
          setTransientPreviewOpen(false)
          setTransientEditDrawerOpen(true)
        }}
        onCheckOut={data.transientStay?.status === 'checked_in' ? () => {
          setTransientPreviewOpen(false)
          setCheckOutConfirmOpen(true)
        } : undefined}
      />

      {/* Transient Stay Edit Drawer (opened from TransientStayDetailPanel's Edit button) */}
      {data.transientStay && (
        <TransientStayMutateDrawer
          open={transientEditDrawerOpen}
          onOpenChange={setTransientEditDrawerOpen}
          transientStayId={data.transientStay.id}
        />
      )}

      {/* Create Lease Drawer (from +Lease button) */}
      <LeaseMutateDrawer
        open={createLeaseOpen}
        onOpenChange={setCreateLeaseOpen}
        lockedLocationId={locationId ?? undefined}
      />

      {/* Create Transient Stay Drawer (from +Stay button) */}
      <TransientStayMutateDrawer
        open={createStayOpen}
        onOpenChange={setCreateStayOpen}
      />

      {/* Away Details Preview (Card → Preview → Edit pattern) */}
      <AwayDetailsPreview
        open={awayPreviewOpen}
        onOpenChange={setAwayPreviewOpen}
        data={data}
      />

      {/* Schedule Create Dialog (from overflow menu) */}
      <ScheduleEditDialog
        open={scheduleCreateOpen}
        onOpenChange={setScheduleCreateOpen}
        schedule={null}
        aircraftId={data.aircraft.id}
        locationId={locationId ?? undefined}
      />

      {/* Fuel Transaction Drawer (from quick-log toolbar) */}
      {locationId && (
        <FuelTransactionDrawer
          open={fuelDrawerOpen}
          onOpenChange={setFuelDrawerOpen}
          locationId={locationId}
          prefill={{
            aircraftId: data.aircraft.id,
            clientId: data.leaseContract?.clientId ?? data.aircraft.owner?.id ?? null,
            leaseContractId: data.leaseContract?.id ?? null,
            transientStayId: data.transientStay?.id ?? null,
            fuelPricePerGallon: data.leaseContract?.fuelPricePerGallon ?? null,
            pricingType: mapLeasePriceType(data.leaseContract?.fuelPriceType),
          }}
        />
      )}
    </div>
  )
}
